import { Action, createReducer, on } from '@ngrx/store';
import { cloneDeep } from 'lodash';

import * as AdminActions from './admin.actions';

import { YieldCurve } from '@shared/models';

export const adminFeatureKey = 'admin';

export interface AdminState {
  regionUpload: {
    kmlGeoJSON: string;
    fileName: string;
  };
  yieldCurveUpload: {
    yieldCurveData: Array<YieldCurve>;
    fileName: string;
  };
  pageFilters: [
    {
      page: string;
      activeFilters: any;
      sortBy: any;
      filters: {
        ownerQuery: string;
        delegateQuery: string;
        showTerminatedQuery: string;
        showAssignedToMeQuery: string;
        regionQuery: string;
        roleQuery: string;
      };
      offset: number;
      currentPage: number;
      paginationTotal: number;
      dashboardId?: number;
    }
  ];
}

export const initialState: AdminState = {
  regionUpload: {
    kmlGeoJSON: null,
    fileName: null,
  },
  yieldCurveUpload: {
    yieldCurveData: null,
    fileName: null,
  },
  pageFilters: [
    {
      page: null,
      activeFilters: [],
      sortBy: [],
      filters: {
        ownerQuery: null,
        delegateQuery: null,
        showTerminatedQuery: null,
        showAssignedToMeQuery: null,
        regionQuery: null,
        roleQuery: null,
      },
      offset: 0,
      currentPage: 1,
      paginationTotal: 40,
      dashboardId: null,
    },
  ],
};

const adminReducer = createReducer(
  initialState,

  on(AdminActions.loadAdmins, state => state),
  on(AdminActions.loadAdminsSuccess, (state, action) => state),
  on(AdminActions.loadAdminsFailure, (state, action) => state),
  on(AdminActions.addRegionKMLContent, (state, geoJSONContent) => ({
    ...state,
    regionUpload: {
      ...state.regionUpload,
      fileName: geoJSONContent.data.fileName,
      kmlGeoJSON: geoJSONContent.data.kmlGeoJSON,
    },
  })),
  on(AdminActions.addYieldCurveContent, (state, yieldCurveData) => ({
    ...state,
    yieldCurveUpload: {
      ...state.yieldCurveUpload,
      fileName: yieldCurveData.data.fileName,
      yieldCurveData: yieldCurveData.data.yieldCurveData,
    },
  })),
  on(AdminActions.clearRegionYieldCurveContent, (state, geoJSONContent) => ({
    ...state,
    regionUpload: {
      fileName: null,
      kmlGeoJSON: null,
    },
    yieldCurveUpload: {
      fileName: null,
      yieldCurveData: null,
    },
  })),
  on(AdminActions.setDashboardState, (state, action) => {
    const pageFilters = cloneDeep(state.pageFilters);
    let index = pageFilters.findIndex(i => i.page === action.page);
    let newAction = cloneDeep(action);
    newAction.state.dashboardId = action.dashboardId;
    if (index !== -1) {
      pageFilters[index] = newAction.state
    } else {
      pageFilters.push(newAction.state);
    }
    return {
      ...state,
      pageFilters,
    };
  }),
  on(AdminActions.setDashboardProperty, (state, action) => {
    const pageFilters = cloneDeep(state.pageFilters);
    let page = getPageFilters(pageFilters, action.page);
    page[action.property] = action.value;
    return {
      ...state,
      pageFilters,
    };
  }),
  on(AdminActions.setDashboardFilter, (state, action) => {
    const pageFilters = cloneDeep(state.pageFilters);
    let page = getPageFilters(pageFilters, action.page);
    page.filters[action.filter] = action.value;
    return {
      ...state,
      pageFilters,
    };
  }),
  on(AdminActions.setDashboardPage, (state, action) => {
    const pageFilters = cloneDeep(state.pageFilters);
    let page = getPageFilters(pageFilters, action.page);
    page.offset = action.offset;
    page.currentPage = action.currentPage;
    page.paginationTotal = action.paginationTotal;
    return {
      ...state,
      pageFilters,
    };
  }),
  on(AdminActions.clearDashboardFilters, (state, action) => {
    const pageFilters = cloneDeep(state.pageFilters);
    let page = getPageFilters(pageFilters, action.page);
    page.filters = {};
    page.offset = initialState.pageFilters[0].offset;
    page.currentPage = initialState.pageFilters[0].currentPage;
    page.paginationTotal = initialState.pageFilters[0].paginationTotal;
    return {
      ...state,
      pageFilters,
    };
  })
);

export function reducer(state: AdminState | undefined, action: Action) {
  return adminReducer(state, action);
}

function getPageFilters(pageFilters: any, pageName: string) {
  let page = pageFilters.find(p => p.page === pageName);
  if (!page) {
    page = cloneDeep(initialState.pageFilters[0]);
    page.page = pageName;
    pageFilters.push(page);
  }
  return page;
}
